/**
 * TWToggle
 *
 * Wiederverwendbarer Toggle-Switch. Button-basiert, mit inline transform
 * für garantiert flüssige Transition zwischen beiden Positionen.
 *
 * Props:
 *   checked     - aktueller Zustand
 *   onChange    - Callback mit neuem Zustand
 *   disabled?   - deaktiviert Interaktion (cursor-wait, kein visueller Sprung)
 *   label?      - zugängliches Label (aria-label)
 *   title?      - Tooltip
 *   color?      - Akzentfarbe im eingeschalteten Zustand (default: 'green')
 *   className?  - zusätzliche Klassen am Wrapper
 */

import React from 'react';

export type TWToggleColor = 'green' | 'brand' | 'indigo' | 'red';
export type TWToggleSize = 'sm' | 'md';

interface TWToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
  title?: string;
  color?: TWToggleColor;
  /** 'md' (default, 11×6) oder 'sm' (8×4) — kompakte Variante für Listen. */
  size?: TWToggleSize;
  className?: string;
}

const SIZE_MAP: Record<
  TWToggleSize,
  { track: string; thumb: string; thumbTx: string; iconBox: string }
> = {
  md: {
    track: 'w-11 h-6',
    thumb: 'size-5',
    thumbTx: 'translateX(1.25rem)',
    iconBox: 'size-3',
  },
  sm: {
    track: 'w-8 h-[18px]',
    thumb: 'size-[14px]',
    thumbTx: 'translateX(0.875rem)',
    iconBox: 'size-2',
  },
};

const COLOR_MAP: Record<TWToggleColor, { on: string; iconOn: string; ring: string }> = {
  green: { on: 'bg-green-500', iconOn: 'text-green-500', ring: 'focus-visible:ring-green-500' },
  brand: { on: 'bg-brand', iconOn: 'text-brand', ring: 'focus-visible:ring-brand' },
  indigo: { on: 'bg-indigo-500', iconOn: 'text-indigo-500', ring: 'focus-visible:ring-indigo-500' },
  red: { on: 'bg-red-500', iconOn: 'text-red-500', ring: 'focus-visible:ring-red-500' },
};

const TWToggleBase: React.FC<TWToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  title,
  color = 'green',
  size = 'md',
  className = '',
}) => {
  const colors = COLOR_MAP[color];
  const sizes = SIZE_MAP[size];
  const resolvedTitle = title ?? (label ? `${label} (${checked ? 'an' : 'aus'})` : undefined);

  const handleClick = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={resolvedTitle}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{ WebkitTapHighlightColor: 'transparent' }}
      className={`relative inline-flex ${sizes.track} shrink-0 items-center rounded-full p-0.5
        ring-1 ring-inset ring-gray-300/60 dark:ring-white/10
        transition-colors duration-200 ease-in-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900
        disabled:cursor-wait
        ${checked ? colors.on : 'bg-gray-200 dark:bg-white/10'}
        ${colors.ring}
        ${className}`}
    >
      <span
        aria-hidden="true"
        style={{
          transform: checked ? sizes.thumbTx : 'translateX(0)',
          // Explizite Transition, da globale CSS-Regel (indexTW.css) die
          // Tailwind transition-transform-Klasse bei .bg-white überschreibt.
          transition: 'transform 200ms ease-in-out',
        }}
        className={`pointer-events-none relative inline-block ${sizes.thumb} rounded-full bg-white
          shadow-sm ring-1 ring-gray-900/5`}
      >
        {/* Off-Icon (X) */}
        <span
          className={`absolute inset-0 flex size-full items-center justify-center
            transition-opacity duration-200 ease-in-out
            ${checked ? 'opacity-0' : 'opacity-100'}`}
        >
          <svg fill="none" viewBox="0 0 12 12" className={`${sizes.iconBox} text-gray-600`}>
            <path
              d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {/* On-Icon (Check) */}
        <span
          className={`absolute inset-0 flex size-full items-center justify-center
            transition-opacity duration-200 ease-in-out
            ${checked ? 'opacity-100' : 'opacity-0'}`}
        >
          <svg fill="currentColor" viewBox="0 0 12 12" className={`${sizes.iconBox} ${colors.iconOn}`}>
            <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
          </svg>
        </span>
      </span>
    </button>
  );
};

export const TWToggle = React.memo(TWToggleBase);

export default TWToggle;
